import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserCard } from "./UserCard.jsx";
import userImg from "../navbar/base.svg";
import { useAuth } from "../../contexts/AuthContext.js";
import { setNavHeight } from "../../redux/navbar/navHeightSlice.js";
import { useDispatch } from "react-redux";
import routeConfig from "./navbarRouteConfig.js";
import Notification from "./NotificationIcon.jsx";

const customerMenuOptions = {
  Proposal: ["Proposals Approval"],
  Invoicing: ["Open Invoices", "Invoice History"],
  Reports: ["Dispatches", "Trouble Calls", "Call Log"],
};

// Define dropdown menu options and their sub-options
const userMenuOptions = {
  "Call Center": [
    "Call Center List",
    "Open Items List",
    "Call Center",
    "Trouble Call Entry",
    "Dispatch Entry",
    "History",
    "Trouble Calls",
    "Dispatches",
    "Employee Log",
    "Time Ticket Entry and Edit",
    "Action Items",
    "Survey Request Entry",
    "Dispatch Hours Building Review",
    "Dispatch Reporter",
  ],
  "Maintenance Contracts": [
    "Maintenance Contracts List",
    "Maintenance Contracts Dashboard",
    "Parent Contract Set Up",
    "Maintenance Contracts Markups",
    "Maintenance Contracts Holds",
    "Maintenance Invoicing",
    "Invoicing Email Set Up",
    "Maintenance Route Planning",
    "Route Summary",
    "Route Summary Log",
    "Route Analysis",
    "Account Analysis",
    "Scheduled Maintenance",
  ],
  Inspections: [
    "Violations List",
    "Inspections List",
    "Inspections Devices List",
    "Tests Due",
    "DOB Now Connect",
    "DOB Violation Tracking",
    "Violations Entry",
    "Inspections Emailer",
    "Agencies List",
    "Service Companies List",
    "Account Pricing List",
  ],
  Invoicing: [
    "Invoice List",
    "Invoice Wizard",
    "Invoice Entry",
    "Invoice Print and Post",
    "Auto Invoicing",
    "Invoice Finance Charges",
    "Invoice Emailer",
    "Invoice Viewer",
  ],
  Proposal: [
    "Proposals & Jobs Dashboard",
    "Job Cost List",
    "Proposal System Setup",
    "Bids Entry and Edit",
    "Proposal Log",
    "Accepted Proposals/Job",
    "Proposal Templates",
    "Proposal Items",
    "Item List",
    "Item Detail Entry",
    "Job Schedule",
    "Proposal Wizard",
    "Job Billing Review - Progress Billing",
    "Jobs Monthly Earned Revenue",
  ],
  "Set Up": [
    "Management Companies",
    "Customers",
    "Buildings",
    "Devices",
    "Rolodex",
  ],
  //Email System Reports
  // Report Emailer
  // GL Reporting Tool
  // Dispatch Labor Analysis
  // Sales Summary
  // Use Tax Calculator
  // Building Sites
  // Calls and Dispatching
  // Cash Receipts
  // Customers
  // Maintenance Contracts
  // Management Reports
  // Proposals
  // System Reports
  // Route Mapper
  Reports: [
    "Route Mapper",
    "Email System Reports",
    "Report Emailer",
    "GL Reporting Tool",
    "Dispatch Labor Analysis",
    "Sales Summary",
    "Use Tax Calculator",
    "Building Sites",
    "Calls and Dispatching",
    "Cash Receipts",
    "Customers",
    "Maintenance Contracts",
    "Management Reports",
    "Proposals",
    "System Reports",
  ],
};

// Define a list of pages to be disabled.
const disabledPages = [
  // call center
  "Open Items List",
  "Proposal System Setup",
  "Trouble Calls",
  "Dispatches",
  "Action Items",
  "Dispatch Reporter",
  // maintenance
  "Maintenance Contracts Markups",
  "Maintenance Contracts Holds",
  "Maintenance Invoicing",
  "Route Summary Log",
  "Account Analysis",
  // invoicing
  "Invoice Print and Post",
  "Auto Invoicing",
  "Invoice Emailer",
  "Invoice Viewer",
  // proposal
  "Job Cost List",
  "Proposal System Setup",
  "Bids Entry and Edit",
  "Proposal Log",
  "Accepted Proposals/Job",
  "Proposal Templates",
  "Proposal Items",
  "Item List",
  "Item Detail Entry",
  "Job Schedule",
  "Job Billing Review - Progress Billing",
  "Jobs Monthly Earned Revenue",
  // reports
  "Email System Reports",
  "Report Emailer",
  "GL Reporting Tool",
  "Dispatch Labor Analysis",
  "Sales Summary",
  "Use Tax Calculator",
  "Building Sites",
  "Calls and Dispatching",
  "Cash Receipts",
  "Maintenance Contracts",
  "Management Reports",
  "Proposals",
  "System Reports",
];

const Navbar = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [isUserCardVisible, setUserCardVisible] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);

  const [menuOptions, setMenuOptions] = useState(
    user?.role === "APPROVER" ? customerMenuOptions : userMenuOptions
  );

  const dispatch = useDispatch();

  useEffect(() => {
    setMenuOptions(
      user?.role === "APPROVER" ? customerMenuOptions : userMenuOptions
    );
  }, [user?.role]);

  // Helper to measure the nav's offsetHeight and store it in Redux
  const measureNavHeight = () => {
    const navbar = document.querySelector("nav");
    if (navbar) {
      dispatch(setNavHeight(navbar.offsetHeight));
    }
  };

  // Toggle the selected menu option, then recalc the nav height
  const toggleDropdown = (menuName) => {
    setSelectedMenu((prev) => (prev === menuName ? null : menuName));
    // Wait for DOM update, then measure
    setTimeout(() => {
      measureNavHeight();
    }, 0);
  };

  // Handle click on a submenu option.
  const handleMenuClick = (menuName, option) => {
    // Check if the option is disabled; if so, do nothing.
    if (disabledPages.includes(option)) {
      console.warn(`${option} is currently disabled.`);
      return;
    }
    console.log(`Selected ${option} under ${menuName}`);
    const matchingRoute = Object.keys(routeConfig).find(
      (path) => routeConfig[path] === option
    );
    if (matchingRoute) {
      navigate(matchingRoute);
      // Minimize the submenu after navigation.
      setSelectedMenu(null);
      // Also recalc nav height after collapse
      setTimeout(() => {
        measureNavHeight();
      }, 0);
    } else {
      console.warn(`No matching route for option: ${option}`);
    }
  };

  // Add a debounced window resize listener to update navbar height
  useEffect(() => {
    let debounceTimeout;
    const handleResize = () => {
      measureNavHeight();
    };

    window.addEventListener("resize", handleResize);
    // Initial measurement on mount
    measureNavHeight();

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(debounceTimeout);
    };
  }, [dispatch]);

  return (
    <nav
      className="fixed top-0 left-0 w-full bg-white shadow-md"
      style={{ zIndex: 1500 }}
    >
      {isAuthenticated ? (
        <>
          {/* Main Header */}
          <div className="flex items-center justify-between text-3xl px-4 py-2 bg-white">
            <div className="flex items-center">
              <div
                className="cursor-pointer"
                onClick={() => {
                  if (user?.role === "APPROVER") {
                    navigate("/customer");
                  } else {
                    navigate("/dashboard");
                  }
                }}
              >
                <img
                  src="/assets/navbar/sss-icon.png"
                  alt="Site Service Software Logo"
                  className="h-14 w-14 mr-6 rounded-sm cursor-pointer"
                />
              </div>
              <h1 className="text-sss-blue-sel font-bold">
                {user?.role !== "APPROVER" ? (
                  "Site Service Software"
                ) : (
                  <>
                    Champion Elevator
                    <span className="text-sm text-gray-500 font-normal ml-2 font-bold">
                      powered by Site Service
                    </span>
                  </>
                )}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {/* Help Icon as SVG */}
              <button className="focus:outline-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-gray-500 cursor-pointer"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M12 14a3 3 0 110-6 3 3 0 010 6z"
                  />
                </svg>
              </button>

              {user?.role !== "APPROVER" && (
                <Notification navigate={navigate} />
              )}

              {/* Profile Image */}
              <img
                id="avatar"
                src={user?.profilePictureUrl || userImg}
                alt="User Icon"
                className="h-10 w-10 rounded-full cursor-pointer"
                onClick={() => setUserCardVisible(!isUserCardVisible)}
              />
              {isUserCardVisible && (
                <UserCard
                  user={user}
                  onClose={() => setUserCardVisible(false)}
                  onLogout={logout}
                />
              )}
            </div>
          </div>
          {/* Divider Line Under Main Header */}

          {/* {user?.role !== "APPROVER" && ( */}
          <>
            <div className="border-b border-gray-200"></div>

            <div className="bg-white-50 py-2 px-4">
              <div className="flex flex-col sm:flex-row sm:items-center">
                <div className="flex items-center">
                  <span
                    className={`font-bold text-2xl ${selectedMenu ? "text-gray-500" : "text-black"
                      }`}
                  >
                    {user?.role !== "APPROVER"
                      ? "Champion Elevator"
                      : user?.firstName || user?.lastName
                        ? `${user.firstName || ""} ${user.lastName || ""}`
                        : "CUSTOMER PORTAL"}
                  </span>
                  <span className="text-gray-500 font-bold mx-2">|</span>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center">
                  {Object.keys(menuOptions).map((menuName) => (
                    <button
                      key={menuName}
                      onClick={() => toggleDropdown(menuName)}
                      className={`my-1 sm:mx-4 focus:outline-none font-bold flex items-center ${selectedMenu === menuName
                        ? "text-black"
                        : "text-gray-500"
                        }`}
                    >
                      {menuName}
                      <svg
                        className={`w-4 h-4 ml-1 transition-transform duration-200 ${selectedMenu === menuName ? "rotate-180" : ""
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {selectedMenu && (
              <div className="bg-white shadow-lg py-2 px-4">
                <div className="flex flex-wrap">
                  {menuOptions[selectedMenu].map((option) => {
                    // Check if the current option is in the disabled list.
                    const isDisabled = disabledPages.includes(option);
                    return (
                      <button
                        key={option}
                        // Only attach the click handler if not disabled.
                        onClick={
                          isDisabled
                            ? null
                            : () => handleMenuClick(selectedMenu, option)
                        }
                        // Apply different styles if disabled.
                        className={`px-4 py-2 text-sm whitespace-nowrap ${isDisabled
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-700 hover:underline"
                          }`}
                        disabled={isDisabled}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
          {/* )} */}
        </>
      ) : (
        <div className="h-16" />
      )}
    </nav>
  );
};

export default Navbar;
